/**
 * Route admin `/tutor/admin/toolbox` — la Boîte à outils côté admin.
 *
 * Tous les outils du catalogue (`features/toolbox/catalog.ts`), paginés, avec
 * recherche (outil ou SKILLCARD reliée), filtre de niveau (l'outil sert à ce
 * niveau) et filtre des liens. Chaque outil s'ouvre pour de vrai dans la
 * fenêtre de la Boîte à outils, au niveau choisi, et liste les SKILLCARDS qui
 * lui sont reliées (`card_tools`, lien vers l'éditeur de la carte, où l'on
 * ajoute ou retire des outils).
 */

import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ChevronDown, Search, Wrench } from 'lucide-react';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { CARD_STATUS_LABELS, type CardStatus } from '@jeraisonne/db/cards';
import { cn, MathText, Paginator } from '@jeraisonne/ui';
import { formatCardLevels } from '@/features/cards/card-faces';
import { TOOLS, type ToolDef, type ToolId } from '@/features/toolbox/catalog';
import { TOOL_ICONS } from '@/features/toolbox/tool-list';
import { ToolboxSheet } from '@/features/toolbox/toolbox-sheet';
import {
  FAMILY_LABEL,
  defaultTestLevel,
  filterTools,
  levelRangeText,
  levelsOf,
  normSearch,
  type LinkFilter,
} from '@/features/toolbox/admin-filter';
import { useAdminToolLinks, type ToolLinkedCard } from '@/lib/admin/toolbox-links';
import { usePagedList } from '@/lib/use-paged-list';

const LEVEL_LABEL: Record<string, string> = Object.fromEntries(
  SCHOOL_LEVELS.map((l) => [l.value, l.label]),
);
/** libellés courts des pastilles de niveau (deux lignes sur 375 px) */
const LEVEL_SHORT: Record<string, string> = {
  '6e': '6e',
  '5e': '5e',
  '4e': '4e',
  '3e': '3e',
  '2nde': '2nde',
  '1re': '1re',
  tle: 'Tle',
  mpsi: 'MPSI',
  ptsi: 'PTSI',
  mp: 'MP',
  pt: 'PT',
};

const CARD_STATUS_STYLES: Record<CardStatus, string> = {
  draft: 'bg-ink-100 text-ink-600',
  generated: 'bg-primary-50 text-primary-600',
  review: 'bg-accent-50 text-accent-600',
  published: 'bg-xp-100 text-xp-700',
  archived: 'bg-ink-100 text-ink-400',
};

const LINK_FILTERS: ReadonlyArray<{ value: LinkFilter; label: string }> = [
  { value: 'all', label: 'Tous' },
  { value: 'linked', label: 'Reliés' },
  { value: 'unlinked', label: 'Non reliés' },
];

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'font-display min-h-11 rounded-full px-3 py-1 text-[13px] font-semibold transition-colors active:scale-[0.98]',
        active ? 'bg-primary-500 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200',
      )}
    >
      {children}
    </button>
  );
}

function LinkedCards({ cards, query }: { cards: ToolLinkedCard[]; query: string }) {
  const words = normSearch(query).split(' ').filter(Boolean);
  const hit = (c: ToolLinkedCard) =>
    words.length > 0 &&
    words.every((w) => normSearch(`${c.title} ${c.domain ?? ''} ${c.shortCode ?? ''}`).includes(w));
  // les cartes qui répondent à la recherche d'abord
  const list = [...cards].sort((a, b) => Number(hit(b)) - Number(hit(a)));
  if (cards.length === 0)
    return (
      <p className="text-ink-500 px-1 py-2 text-[13px]">
        Aucune SkillCard reliée. Les liens se font depuis l’éditeur d’une carte (section « Outils
        »).
      </p>
    );
  return (
    <ul className="divide-ink-100 divide-y" data-testid="admin-linked-cards">
      {list.map((c) => (
        <li key={c.id}>
          <Link
            to="/tutor/cards/$cardId"
            params={{ cardId: c.id }}
            className={cn(
              'hover:bg-ink-50 flex min-h-11 items-center gap-2 rounded-lg px-2 py-1.5',
              hit(c) && 'bg-accent-50',
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="text-ink-900 block truncate text-[13px] font-semibold">
                <MathText>{c.title}</MathText>
              </span>
              <span className="text-ink-500 block truncate text-xs">
                {[c.levels.length ? formatCardLevels(c.levels) : null, c.domain, c.shortCode]
                  .filter(Boolean)
                  .join(' · ')}
                {c.position > 0 ? ` · outil n° ${c.position + 1} de la carte` : ''}
              </span>
            </span>
            {c.status && c.status !== 'published' && (
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-xs font-bold',
                  CARD_STATUS_STYLES[c.status as CardStatus] ?? 'bg-ink-100 text-ink-600',
                )}
              >
                {CARD_STATUS_LABELS[c.status as CardStatus] ?? c.status}
              </span>
            )}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ToolRow({
  tool,
  cards,
  filterLevel,
  query,
  onTest,
}: {
  tool: ToolDef;
  cards: ToolLinkedCard[] | undefined;
  filterLevel: string | null;
  query: string;
  onTest: (id: ToolId, level: string) => void;
}) {
  const Icon = TOOL_ICONS[tool.id] ?? Wrench;
  const levels = levelsOf(tool);
  const [level, setLevel] = useState(() => defaultTestLevel(tool, filterLevel));
  const [open, setOpen] = useState(false);
  // la recherche vise une carte reliée : la liste s'ouvre d'elle-même
  const words = normSearch(query).split(' ').filter(Boolean);
  const toolHit =
    words.length === 0 ||
    words.every((w) =>
      normSearch(
        [tool.title, tool.pitch, tool.id, ...tool.families.map((f) => FAMILY_LABEL[f])].join(' '),
      ).includes(w),
    );
  const expanded = open || (!toolHit && (cards?.length ?? 0) > 0);
  const n = cards?.length;
  const listId = `admin-tool-${tool.id}-cards`;
  return (
    <li className="p-3" data-testid={`admin-tool-${tool.id}`}>
      <div className="flex items-start gap-3">
        <div className="bg-ink-100 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
          <Icon className="text-ink-700 h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-ink-900 text-[15px] font-bold">{tool.title}</h2>
          <p className="text-ink-600 mt-0.5 text-[13px]">{tool.pitch}</p>
          <p className="text-ink-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span className="font-semibold">{levelRangeText(tool)}</span>
            <span aria-hidden>·</span>
            <span>{tool.families.map((f) => FAMILY_LABEL[f]).join(', ')}</span>
            <span aria-hidden>·</span>
            <code className="bg-ink-100 rounded px-1">{tool.id}</code>
          </p>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 sm:pl-[3.25rem]">
        <label className="text-ink-600 flex items-center gap-1.5 text-xs font-semibold">
          Niveau
          <select
            value={level}
            onChange={(e) => setLevel(e.target.value)}
            className="border-ink-200 text-ink-800 min-h-11 rounded-xl border bg-white px-2 text-sm"
            aria-label={`Niveau du test de ${tool.title}`}
          >
            {levels.map((l) => (
              <option key={l} value={l}>
                {LEVEL_LABEL[l] ?? l}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => onTest(tool.id, level)}
          className="bg-primary-500 hover:bg-primary-600 min-h-11 rounded-xl px-4 text-sm font-bold text-white active:scale-[0.98]"
          aria-label={`Tester ${tool.title}`}
        >
          Tester
        </button>
        <button
          type="button"
          onClick={() => setOpen(!expanded)}
          aria-expanded={expanded}
          aria-controls={listId}
          className="border-ink-200 text-ink-700 hover:bg-ink-50 flex min-h-11 items-center gap-1.5 rounded-xl border px-3 text-sm font-semibold active:scale-[0.98]"
        >
          {n === undefined ? 'SkillCards…' : `SkillCards (${n})`}
          <ChevronDown
            className={cn('h-4 w-4 transition-transform', expanded && 'rotate-180')}
            aria-hidden
          />
        </button>
      </div>
      {expanded && (
        <div id={listId} className="mt-2 sm:pl-[3.25rem]">
          <LinkedCards cards={cards ?? []} query={query} />
        </div>
      )}
    </li>
  );
}

export function TutorAdminToolboxPage() {
  const { data: links, isLoading, error } = useAdminToolLinks();
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState<string | null>(null);
  const [linkFilter, setLinkFilter] = useState<LinkFilter>('all');
  const [testing, setTesting] = useState<{ id: ToolId; level: string } | null>(null);

  const count = (id: string) => links?.[id]?.length ?? 0;
  const words = normSearch(query).split(' ').filter(Boolean);
  const filtered = useMemo(() => {
    const byTool = filterTools(
      TOOLS,
      { query: '', level, links: links ? linkFilter : 'all' },
      count,
    );
    if (words.length === 0) return byTool;
    // recherche : l'outil lui-même, ou une SkillCard qui lui est reliée
    const toolHits = new Set(filterTools(byTool, { query, level: null, links: 'all' }, count));
    return byTool.filter(
      (t) =>
        toolHits.has(t) ||
        (links?.[t.id] ?? []).some((c) =>
          words.every((w) =>
            normSearch(`${c.title} ${c.domain ?? ''} ${c.shortCode ?? ''}`).includes(w),
          ),
        ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links, query, level, linkFilter]);
  const { items, paginator } = usePagedList(filtered, {
    resetKeys: [query, level, linkFilter],
    unit: 'outils',
    label: 'Pages des outils',
  });

  const totals = useMemo(() => {
    const all = Object.values(links ?? {}).flat();
    return { links: all.length, cards: new Set(all.map((c) => c.id)).size };
  }, [links]);
  const testedTool = testing ? TOOLS.find((t) => t.id === testing.id) : null;

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <div className="mb-4">
        <h1 className="font-display text-ink-900 text-[22px] font-extrabold">Boîte à outils</h1>
        <p className="text-ink-500 mt-0.5 text-[13px]" data-testid="admin-toolbox-totals">
          {`${TOOLS.length} outils`}
          {links ? ` · ${totals.links} liens vers ${totals.cards} SkillCards` : ''}
        </p>
      </div>

      <div className="mb-4 space-y-2">
        <label className="relative block">
          <span className="sr-only">Rechercher un outil ou une SkillCard reliée</span>
          <Search
            className="text-ink-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Outil ou SkillCard reliée"
            className="border-ink-200 text-ink-900 focus:border-primary-400 min-h-11 w-full rounded-xl border bg-white py-2 pl-9 pr-3 text-[15px] outline-none"
          />
        </label>
        <div role="group" aria-label="Niveau" className="flex flex-wrap items-center gap-1.5">
          <span className="text-ink-500 mr-1 text-xs font-semibold" aria-hidden>
            Niveau
          </span>
          <Pill active={level === null} onClick={() => setLevel(null)}>
            Tous
          </Pill>
          {SCHOOL_LEVELS.map((l) => (
            <Pill
              key={l.value}
              active={level === l.value}
              onClick={() => setLevel(level === l.value ? null : l.value)}
            >
              <span aria-hidden>{LEVEL_SHORT[l.value] ?? l.label}</span>
              <span className="sr-only">{l.label}</span>
            </Pill>
          ))}
        </div>
        <div
          role="group"
          aria-label="Liens aux SkillCards"
          className="flex flex-wrap items-center gap-1.5"
        >
          <span className="text-ink-500 mr-1 text-xs font-semibold" aria-hidden>
            SkillCards
          </span>
          {LINK_FILTERS.map((f) => (
            <Pill
              key={f.value}
              active={linkFilter === f.value}
              onClick={() => setLinkFilter(f.value)}
            >
              {f.label}
            </Pill>
          ))}
        </div>
      </div>

      {error && (
        <p className="bg-error-50 text-ink-800 mb-3 rounded-xl px-3 py-2 text-[13px]" role="alert">
          {`Liens SkillCards indisponibles : ${(error as Error).message}`}
        </p>
      )}
      <p
        className="text-ink-500 mb-2 text-[13px]"
        aria-live="polite"
        data-testid="admin-toolbox-count"
      >
        {`${filtered.length} outil${filtered.length > 1 ? 's' : ''}`}
        {isLoading ? ' · chargement des liens…' : ''}
      </p>

      {filtered.length === 0 ? (
        <div className="py-12 text-center">
          <Wrench className="text-ink-300 mx-auto mb-3 h-10 w-10" aria-hidden />
          <p className="font-display text-ink-600 font-semibold">Aucun outil</p>
          <p className="text-ink-400 mt-1 text-[13px]">
            Aucun outil ne correspond à la recherche et aux filtres.
          </p>
        </div>
      ) : (
        <>
          <ul className="border-ink-200 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
            {items.map((t) => (
              <ToolRow
                key={`${t.id}|${level ?? ''}`}
                tool={t}
                cards={links?.[t.id] ?? (links ? [] : undefined)}
                filterLevel={level}
                query={query}
                onTest={(id, l) => setTesting({ id, level: l })}
              />
            ))}
          </ul>
          <Paginator {...paginator} />
        </>
      )}

      {testing && testedTool && (
        <ToolboxSheet
          context={{ level: testing.level, families: testedTool.families, linked: [testing.id] }}
          initialTool={testing.id}
          featuredTitle="Outil testé"
          onClose={() => setTesting(null)}
        />
      )}
    </div>
  );
}
